import React, { useEffect, useState, useCallback } from 'react';
import { getCampaigns, createCampaign, updateCampaign, deleteCampaign, getClients, exportCampaignsCSV, requestInvoice, getInvoiceRequests, bulkDeleteCampaigns, importCampaignsCSV, getActivityLog } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Plus, Pencil, Trash2, Search, CalendarClock, Download, FileText, CheckCircle, Clock, Upload, History } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { CampaignFormDialog, BUY_TYPES, STATUSES, CURRENCY_SYMBOLS, STATUS_COLORS, BUY_TYPE_COLORS, calcBudget, calcDailyRunRate } from '../components/campaigns/CampaignFormDialog';
import { InvoiceRequestDialog } from '../components/InvoiceRequestDialog';
import { CsvImportDialog } from '../components/CsvImportDialog';
import { ActivityLogDialog } from '../components/ActivityLogDialog';

const emptyForm = { name: '', client_id: '', buy_type: '', status: 'draft', volume: '', price: '', currency: 'USD', start_date: '', end_date: '', description: '' };

export default function CampaignsPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterBuyType, setFilterBuyType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  // Form state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  // Invoice state
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [invoiceCampaign, setInvoiceCampaign] = useState(null);
  const [invoiceForm, setInvoiceForm] = useState({ buy_type: '', volume: '', price: '', currency: 'USD', start_date: '', end_date: '', notes: '', send_to_emails: '' });
  const [sendingInvoice, setSendingInvoice] = useState(false);
  const [invoiceStatuses, setInvoiceStatuses] = useState({});

  // Bulk state
  const [selected, setSelected] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Import state
  const [importOpen, setImportOpen] = useState(false);
  const [importing, setImporting] = useState(false);

  // Activity log state
  const [logOpen, setLogOpen] = useState(false);
  const [logCampaign, setLogCampaign] = useState(null);
  const [activityLog, setActivityLog] = useState([]);
  const [logLoading, setLogLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [campRes, clientRes, invRes] = await Promise.all([getCampaigns(), getClients(), getInvoiceRequests()]);
      setCampaigns(campRes.data);
      setClients(clientRes.data);
      const statusMap = {};
      for (const inv of invRes.data) {
        const cid = inv.campaign_id;
        if (!statusMap[cid] || inv.created_at > statusMap[cid].created_at) statusMap[cid] = inv;
      }
      setInvoiceStatuses(statusMap);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // CRUD
  const openCreate = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ name: c.name, client_id: c.client_id, buy_type: c.buy_type || '', status: c.status, volume: String(c.volume || ''), price: String(c.price || ''), currency: c.currency || 'USD', start_date: c.start_date || '', end_date: c.end_date || '', description: c.description || '' });
    setDialogOpen(true);
  };
  const handleSave = async () => {
    setSaving(true);
    try {
      const vol = parseInt(form.volume) || 0;
      const prc = parseFloat(form.price) || 0;
      const budget = calcBudget(vol, prc, form.buy_type);
      const payload = { ...form, budget, volume: vol, price: prc };
      if (editing) { await updateCampaign(editing.id, payload); toast.success('Campaign updated'); }
      else { await createCampaign(payload); toast.success('Campaign created'); }
      setDialogOpen(false); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error saving campaign'); }
    finally { setSaving(false); }
  };
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this campaign?')) return;
    try { await deleteCampaign(id); toast.success('Campaign deleted'); fetchData(); }
    catch { toast.error('Error deleting campaign'); }
  };

  // Invoice
  const openInvoice = (c) => {
    setInvoiceCampaign(c);
    setInvoiceForm({ buy_type: c.buy_type || '', volume: String(c.volume || ''), price: String(c.price || ''), currency: c.currency || 'USD', start_date: c.start_date || '', end_date: c.end_date || '', notes: '', send_to_emails: '' });
    setInvoiceOpen(true);
  };
  const handleSendInvoice = async () => {
    if (!invoiceCampaign) return;
    setSendingInvoice(true);
    try {
      const vol = parseInt(invoiceForm.volume) || 0;
      const prc = parseFloat(invoiceForm.price) || 0;
      const budget = calcBudget(vol, prc, invoiceForm.buy_type);
      const { data } = await requestInvoice({ campaign_id: invoiceCampaign.id, campaign_name: invoiceCampaign.name, client_name: invoiceCampaign.client_name || '', buy_type: invoiceForm.buy_type, volume: vol, price: prc, budget, currency: invoiceForm.currency, start_date: invoiceForm.start_date, end_date: invoiceForm.end_date, notes: invoiceForm.notes, send_to_emails: invoiceForm.send_to_emails.split(',').map(e => e.trim()).filter(Boolean) });
      if (data.emails_failed?.length > 0) toast.warning(`Invoice saved but email failed for: ${data.emails_failed.join(', ')}`);
      else if (data.emails_sent > 0) toast.success(`Invoice request sent to ${data.emails_sent} recipient(s)`);
      else toast.success('Invoice request saved');
      setInvoiceOpen(false); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error sending invoice request'); }
    finally { setSendingInvoice(false); }
  };

  // Bulk
  const toggleSelect = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const handleBulkDelete = async () => {
    if (!window.confirm(`Delete ${selected.size} selected campaign(s)?`)) return;
    setBulkDeleting(true);
    try { const { data } = await bulkDeleteCampaigns([...selected]); toast.success(data.message); setSelected(new Set()); fetchData(); }
    catch { toast.error('Error deleting campaigns'); }
    finally { setBulkDeleting(false); }
  };

  // Import
  const handleImport = async (file) => {
    setImporting(true);
    try {
      const { data } = await importCampaignsCSV(file);
      if (data.errors?.length > 0) toast.warning(`Imported ${data.imported}/${data.total_rows}. ${data.errors.length} error(s).`);
      else toast.success(`${data.imported} campaign(s) imported`);
      setImportOpen(false); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error importing CSV'); }
    finally { setImporting(false); }
  };

  // Activity log
  const openLog = async (c) => {
    setLogCampaign(c); setLogOpen(true); setLogLoading(true);
    try { const { data } = await getActivityLog(c.id); setActivityLog(data); }
    catch { setActivityLog([]); }
    finally { setLogLoading(false); }
  };

  const filtered = campaigns.filter((c) => {
    const matchSearch = [c.name, c.client_name, c.buy_type].some((f) => (f || '').toLowerCase().includes(search.toLowerCase()));
    return matchSearch && (filterBuyType === 'all' || c.buy_type === filterBuyType) && (filterStatus === 'all' || c.status === filterStatus);
  });

  const toggleSelectAll = () => selected.size === filtered.length ? setSelected(new Set()) : setSelected(new Set(filtered.map(c => c.id)));

  if (loading) return <div className="h-64 bg-zinc-100 animate-pulse" data-testid="campaigns-loading" />;

  return (
    <div className="space-y-6" data-testid="campaigns-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-950">Campaigns</h1>
        <div className="flex items-center gap-2">
          <a href={exportCampaignsCSV()} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-3 h-9 border border-zinc-300 text-sm text-zinc-700 hover:bg-zinc-50 transition-all duration-150" data-testid="export-campaigns-csv"><Download size={14} /> Export</a>
          <Button variant="outline" onClick={() => setImportOpen(true)} className="rounded-none border-zinc-300 gap-2" data-testid="import-csv-button"><Upload size={14} /> Import CSV</Button>
          <Button onClick={openCreate} className="rounded-none bg-black text-white hover:bg-zinc-800 gap-2" data-testid="add-campaign-button"><Plus size={16} /> New Campaign</Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-sm"><Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" /><Input placeholder="Search campaigns..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 rounded-none border-zinc-300" data-testid="campaign-search-input" /></div>
        <Select value={filterBuyType} onValueChange={setFilterBuyType}><SelectTrigger className="w-36 rounded-none" data-testid="buy-type-filter"><SelectValue placeholder="Buy Type" /></SelectTrigger><SelectContent><SelectItem value="all">All Buy Types</SelectItem>{BUY_TYPES.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent></Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}><SelectTrigger className="w-32 rounded-none" data-testid="status-filter"><SelectValue placeholder="Status" /></SelectTrigger><SelectContent><SelectItem value="all">All Status</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>)}</SelectContent></Select>
        {selected.size > 0 && <Button onClick={handleBulkDelete} disabled={bulkDeleting} variant="destructive" className="rounded-none gap-2" data-testid="bulk-delete-button"><Trash2 size={14} /> {bulkDeleting ? 'Deleting...' : `Delete ${selected.size} Selected`}</Button>}
      </div>

      {/* Table */}
      <div className="bg-white border border-zinc-200 overflow-x-auto">
        <table className="w-full text-sm border-collapse" data-testid="campaigns-table">
          <thead>
            <tr className="bg-zinc-50 border-b border-zinc-200">
              <th className="px-3 py-2.5 w-10"><Checkbox checked={filtered.length > 0 && selected.size === filtered.length} onCheckedChange={toggleSelectAll} data-testid="select-all-checkbox" /></th>
              <th className="text-left px-4 py-2.5 text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Campaign</th>
              <th className="text-left px-4 py-2.5 text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Client</th>
              <th className="text-left px-4 py-2.5 text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Buy Type</th>
              <th className="text-left px-4 py-2.5 text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Status</th>
              <th className="text-right px-4 py-2.5 text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Budget</th>
              <th className="text-right px-4 py-2.5 text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Volume</th>
              <th className="text-right px-4 py-2.5 text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Price</th>
              <th className="text-right px-4 py-2.5 text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Daily Rate</th>
              <th className="text-left px-4 py-2.5 text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Invoice</th>
              {isAdmin && <th className="text-left px-4 py-2.5 text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Created By</th>}
              <th className="text-right px-4 py-2.5 text-xs font-medium tracking-[0.1em] uppercase text-zinc-500">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={isAdmin ? 12 : 11} className="text-center py-12 text-zinc-400">No campaigns found</td></tr>
            ) : filtered.map((c) => {
              const sym = CURRENCY_SYMBOLS[c.currency] || c.currency || '$';
              const dailyRate = calcDailyRunRate(c.volume, c.start_date, c.end_date);
              return (
                <tr key={c.id} className={`border-b border-zinc-100 hover:bg-zinc-50 transition-colors duration-150 ${selected.has(c.id) ? 'bg-zinc-50' : ''}`} data-testid={`campaign-row-${c.id}`}>
                  <td className="px-3 py-3"><Checkbox checked={selected.has(c.id)} onCheckedChange={() => toggleSelect(c.id)} data-testid={`select-campaign-${c.id}`} /></td>
                  <td className="px-4 py-3 font-medium text-zinc-900">{c.name}</td>
                  <td className="px-4 py-3 text-zinc-600">{c.client_name}</td>
                  <td className="px-4 py-3"><span className="text-xs font-bold px-2 py-0.5" style={{ backgroundColor: (BUY_TYPE_COLORS[c.buy_type] || '#71717A') + '18', color: BUY_TYPE_COLORS[c.buy_type] || '#71717A' }}>{c.buy_type}</span></td>
                  <td className="px-4 py-3"><span className="text-xs font-medium px-2 py-0.5" style={{ backgroundColor: (STATUS_COLORS[c.status] || '#71717A') + '20', color: STATUS_COLORS[c.status] || '#71717A' }}>{c.status}</span></td>
                  <td className="px-4 py-3 text-right font-mono">{sym}{(c.budget || 0).toLocaleString()}</td>
                  <td className="px-4 py-3 text-right font-mono">{(c.volume || 0).toLocaleString()}</td>
                  <td className="px-4 py-3 text-right font-mono">{sym}{(c.price || 0).toLocaleString()}</td>
                  <td className="px-4 py-3 text-right font-mono">{dailyRate ? <span className="flex items-center justify-end gap-1 text-zinc-700"><CalendarClock size={12} className="text-zinc-400" />{Number(dailyRate).toLocaleString()}</span> : <span className="text-zinc-300">-</span>}</td>
                  <td className="px-4 py-3">{invoiceStatuses[c.id] ? (invoiceStatuses[c.id].status === 'raised' ? <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 bg-emerald-100 text-emerald-700"><CheckCircle size={11} /> Raised</span> : <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 bg-amber-100 text-amber-700"><Clock size={11} /> Pending</span>) : <span className="text-zinc-300 text-xs">-</span>}</td>
                  {isAdmin && <td className="px-4 py-3 text-zinc-500 text-xs">{c.created_by_name || '-'}</td>}
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openLog(c)} className="h-8 w-8 p-0 text-zinc-400 hover:text-zinc-700" data-testid={`log-campaign-${c.id}`} title="Activity Log"><History size={14} /></Button>
                      <Button variant="ghost" size="sm" onClick={() => openInvoice(c)} className="h-8 px-2 gap-1 text-xs text-zinc-600 hover:text-zinc-900" data-testid={`invoice-campaign-${c.id}`}><FileText size={13} /> Invoice</Button>
                      <Button variant="ghost" size="sm" onClick={() => openEdit(c)} className="h-8 w-8 p-0" data-testid={`edit-campaign-${c.id}`}><Pencil size={14} /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(c.id)} className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50" data-testid={`delete-campaign-${c.id}`}><Trash2 size={14} /></Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Dialogs */}
      <CampaignFormDialog open={dialogOpen} onOpenChange={setDialogOpen} editing={editing} form={form} setForm={setForm} onSave={handleSave} saving={saving} clients={clients} />
      <InvoiceRequestDialog open={invoiceOpen} onOpenChange={setInvoiceOpen} campaign={invoiceCampaign} form={invoiceForm} setForm={setInvoiceForm} onSend={handleSendInvoice} sending={sendingInvoice} />
      <CsvImportDialog open={importOpen} onOpenChange={setImportOpen} onImport={handleImport} importing={importing} />
      <ActivityLogDialog open={logOpen} onOpenChange={setLogOpen} campaign={logCampaign} logs={activityLog} loading={logLoading} />
    </div>
  );
}
